/**
 * Review inbox (spec §8.11): review items by kind with type-specific actions — promote/reject
 * derived edges, choose a grounding candidate or create a concept, open SHACL focus nodes and
 * dangling links, pick the winning version of an LWW conflict and apply a deprecated term's
 * replacement.
 */
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { useMemo, useState } from "react";
import { describeNodes, useWorkspace } from "../api/hooks";
import type { ReviewItem } from "../api/types";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { blockTarget, useResolveItem, useReviewItems, type ReviewAction } from "./api";
import { Rich, useT } from "../i18n";
import { kindLabel, unitSymbol } from "../lib/quantities";

const KINDS: Array<{ kind: string; label: string }> = [
  { kind: "derived_edge", label: "review.kind.derived_edge" },
  { kind: "grounding", label: "review.kind.grounding" },
  { kind: "shacl_violation", label: "review.kind.shacl_violation" },
  { kind: "dangling_link", label: "review.kind.dangling_link" },
  { kind: "lww_conflict", label: "review.kind.lww_conflict" },
  { kind: "deprecated_term", label: "review.kind.deprecated_term" },
  { kind: "fact_unit", label: "review.kind.fact_unit" },
];

type Resolve = (item: ReviewItem, action: ReviewAction, payload?: Record<string, unknown>) => void;

export function ReviewInboxPage() {
  const { workspaceId } = useParams({ strict: false }) as { workspaceId: string };
  const t = useT();
  const ws = useWorkspace(workspaceId);
  const search = useSearch({ strict: false }) as { kind?: string };
  const [kind, setKind] = useState(search.kind && KINDS.some((k) => k.kind === search.kind) ? search.kind : "derived_edge");
  const [status, setStatus] = useState("open");
  const items = useReviewItems(workspaceId, kind, status);
  const resolveItem = useResolveItem(workspaceId);
  const [error, setError] = useState<string | null>(null);
  const canWrite = !!ws.data && ws.data.my_role !== "viewer";
  const canAct = canWrite && status === "open";

  const iris = useMemo(() => {
    const out = new Set<string>();
    for (const it of items.data?.items ?? []) {
      const p = it.payload as Record<string, unknown>;
      for (const k of ["s", "o", "block", "focusNode", "term", "replacement", "subject", "target"]) if (typeof p[k] === "string") out.add(p[k] as string);
      const about = (p.fact as { about?: string } | undefined)?.about; // [M31] the class a proposed fact is about
      if (about) out.add(about);
    }
    return [...out].sort();
  }, [items.data]);
  const labels = useQuery({
    queryKey: ["reviewLabels", workspaceId, iris],
    enabled: iris.length > 0,
    staleTime: 60_000,
    queryFn: async () => new Map((await describeNodes(workspaceId, iris)).map((d) => [d.iri, d.label])),
  });
  const label = (iri: string) => labels.data?.get(iri) || iri;

  const resolve: Resolve = (item, action, payload) => {
    setError(null);
    resolveItem.mutate(
      { id: item.id, action, payload },
      { onError: (e) => setError(e instanceof Error ? e.message : String(e)) },
    );
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-9 pb-16 pt-8" data-testid="review-inbox">
      <div className="mb-4 flex items-center gap-3">
        <Link to="/w/$workspaceId" params={{ workspaceId }} className="rounded-sm px-2.5 py-1.5 text-[13.5px] text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100">
          {t("page.backToWorkspace")}
        </Link>
        <h1 className="text-[26px] font-semibold">{t("review.title")}</h1>
        <div className="grow" />
        <select className="kss-control kss-field bg-transparent px-3 py-[7px] text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="review-status">
          <option value="open">{t("review.status.open")}</option>
          <option value="resolved">{t("review.status.resolved")}</option>
          <option value="dismissed">{t("review.status.dismissed")}</option>
        </select>
      </div>
      <div className="mb-4 flex flex-wrap gap-1 border-b border-neutral-200 dark:border-neutral-800">
        {KINDS.map((k) => (
          <button
            key={k.kind}
            className={clsx("-mb-px border-b-2 px-3 py-1.5 text-sm", kind === k.kind ? "border-accent font-medium" : "border-transparent text-neutral-500")}
            onClick={() => setKind(k.kind)}
            data-testid={`review-tab-${k.kind}`}
          >
            {t(k.label)}
            {!!items.data?.counts[k.kind] && <span className="ml-1 rounded-full bg-neutral-100 px-1.5 font-mono text-xs text-neutral-500 dark:bg-neutral-800">{items.data.counts[k.kind]}</span>}
          </button>
        ))}
      </div>
      {error && <div className="mb-3 rounded-sm bg-err-tint px-3 py-2 text-sm text-err">{error}</div>}
      {items.isLoading && <div className="text-sm text-neutral-500">{t("common.loading")}</div>}
      {items.error && <div className="text-sm text-err">{t("review.loadError")}</div>}
      {items.data && items.data.items.length === 0 && (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-[var(--surface-2)] px-6 py-12 text-center text-neutral-500 dark:border-neutral-700">{t("review.empty")}</div>
      )}
      <ul className="space-y-3">
        {items.data?.items.map((item) => (
          <li key={item.id} className="kss-surface p-4" data-testid="review-item" data-kind={item.kind} data-item-id={item.id}>
            <ItemBody item={item} workspaceId={workspaceId} label={label} canAct={canAct} canWrite={canWrite} resolve={resolve} busy={resolveItem.isPending} />
            <div className="mt-2 flex items-center gap-2 text-xs text-neutral-500">
              <span>{new Date(item.createdAt).toLocaleString()}</span>
              <div className="grow" />
              {canAct && (
                <Button variant="ghost" onClick={() => resolve(item, "dismiss")} disabled={resolveItem.isPending} data-testid="review-dismiss">
                  {t("review.dismiss")}
                </Button>
              )}
              {item.status !== "open" && <span data-testid="review-item-status" data-status={item.status}>{t(item.status === "resolved" ? "review.status.resolved" : "review.status.dismissed")}</span>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BlockLink({ iri, workspaceId, children }: { iri: string; workspaceId: string; children: React.ReactNode }) {
  const target = blockTarget(iri);
  if (!target) return <span className="font-mono text-xs">{children}</span>;
  return (
    <Link to="/w/$workspaceId/d/$docId" params={{ workspaceId, docId: target.docId }} hash={`block=${encodeURIComponent(target.treeId)}`} className="text-accent underline">
      {children}
    </Link>
  );
}

function ItemBody({ item, workspaceId, label, canAct, canWrite, resolve, busy }: { item: ReviewItem; workspaceId: string; label: (iri: string) => string; canAct: boolean; canWrite: boolean; resolve: Resolve; busy: boolean }) {
  const t = useT();
  const p = item.payload as Record<string, unknown>;
  switch (item.kind) {
    case "fact_unit":
      return <FactUnitItem item={item} p={p} workspaceId={workspaceId} label={label} canAct={canAct} resolve={resolve} busy={busy} />;
    case "derived_edge":
      if (p.fact) return <DerivedFact item={item} p={p} workspaceId={workspaceId} label={label} canAct={canAct} resolve={resolve} busy={busy} />;
      return <DerivedEdge item={item} p={p} workspaceId={workspaceId} label={label} canAct={canAct} resolve={resolve} busy={busy} />;
    case "grounding":
      return <GroundingItem item={item} p={p} workspaceId={workspaceId} label={label} canAct={canAct} resolve={resolve} busy={busy} />;
    case "lww_conflict":
      return <ConflictItem item={item} p={p} workspaceId={workspaceId} canAct={canAct} resolve={resolve} busy={busy} />;
    case "deprecated_term":
      return <DeprecatedTerm item={item} p={p} label={label} canAct={canAct} canWrite={canWrite} resolve={resolve} busy={busy} />;
    case "shacl_violation":
      return (
        <div className="text-sm">
          <div className="font-medium">{String(p.message || t("review.shacl.default"))}</div>
          <div className="mt-1 text-neutral-600 dark:text-neutral-400">
            {t("review.shacl.focus")} <BlockLink iri={String(p.focusNode)} workspaceId={workspaceId}>{label(String(p.focusNode))}</BlockLink>
            {p.resultPath ? <> · {t("review.shacl.path")} <code className="text-xs">{String(p.resultPath)}</code></> : null}
            {p.value ? <> · {t("review.shacl.value")} <code className="text-xs">{String(p.value)}</code></> : null}
          </div>
        </div>
      );
    case "dangling_link": {
      // spec §8.11 [0.49]: which block links where, and what a reader can do about it
      const predicate = String(p.predicate ?? "").replace(/^.*[#/]/, "");
      return (
        <div className="text-sm" data-testid="review-dangling">
          <div className="font-medium">{t("review.dangling.title")}</div>
          <div className="mt-1 text-neutral-600 dark:text-neutral-400">
            {t("review.dangling.from")} <BlockLink iri={String(p.subject)} workspaceId={workspaceId}>{label(String(p.subject))}</BlockLink>{" "}
            <span className="font-mono text-xs">{predicate}</span> {t("review.dangling.to")} <span className="break-all">{label(String(p.target))}</span>
          </div>
          <div className="mt-1 text-xs text-neutral-500">{t(p.reason === "target deleted" ? "review.dangling.deleted" : "review.dangling.missing")}</div>
        </div>
      );
    }
    default:
      return (
        <div className="text-sm">
          <div className="font-medium">{item.kind.replace(/_/g, " ")}</div>
          {item.crdtDocId && item.treeId && (
            <BlockLink iri={`urn:km:blk:${item.crdtDocId}:${item.treeId}`} workspaceId={workspaceId}>{t("review.openBlock")}</BlockLink>
          )}
          <pre className="mt-1 overflow-x-auto text-xs text-neutral-500">{JSON.stringify(p, null, 1)}</pre>
        </div>
      );
  }
}

function DerivedEdge({ item, p, workspaceId, label, canAct, resolve, busy }: { item: ReviewItem; p: Record<string, unknown>; workspaceId: string; label: (iri: string) => string; canAct: boolean; resolve: Resolve; busy: boolean }) {
  const t = useT();
  const predicate = String(p.p).replace(/^.*[#/]/, "");
  const confidence = typeof p.confidence === "number" ? p.confidence : null;
  // a long literal (a file summary, spec §18.5) reads as prose below the edge, not as a quoted token
  const prose = p.objectKind === "literal" && String(p.o).length > 80;
  return (
    <div className="text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <BlockLink iri={String(p.s)} workspaceId={workspaceId}>{label(String(p.s))}</BlockLink>
        <span className="rounded bg-[#e8eef6] px-1.5 text-xs text-accent dark:bg-[#1f2d40] dark:text-[#b7c8e0]" title={String(p.p)}>{predicate}</span>
        {!prose && <span className="font-medium" title={String(p.o)} data-testid="review-edge-object">{p.objectKind === "literal" ? `"${String(p.o)}"` : label(String(p.o))}</span>}
        {confidence !== null && <span className="text-xs text-neutral-500">{t("review.edge.confidence", { value: confidence.toFixed(2) })}</span>}
        <span className="rounded border border-dashed border-neutral-400 px-1 text-[10px] uppercase text-neutral-500">{t("review.edge.derived")}</span>
      </div>
      {prose && (
        <p className="mt-1 max-w-prose whitespace-pre-wrap break-words" data-testid="review-edge-object" data-prose="true">
          {String(p.o)}
        </p>
      )}
      {p.evidence ? <blockquote className="mt-1 border-l-2 border-neutral-300 pl-2 text-neutral-600 dark:text-neutral-400">{String(p.evidence)}</blockquote> : null}
      {canAct && (
        <div className="mt-2 flex gap-2">
          <Button onClick={() => resolve(item, "promote")} disabled={busy} data-testid="review-promote">{t("review.edge.promote")}</Button>
          <Button variant="danger" onClick={() => resolve(item, "reject")} disabled={busy} data-testid="review-reject">{t("review.edge.reject")}</Button>
        </div>
      )}
    </div>
  );
}

interface ProposedFact {
  kind: string;
  value: string;
  unit: string;
  about?: string;
  block: string;
  written?: string;
}

/** [M31] A quantity extraction proposes: *Temperature 60 °C*, on the block or about one of its entities. */
function DerivedFact({ item, p, workspaceId, label, canAct, resolve, busy }: { item: ReviewItem; p: Record<string, unknown>; workspaceId: string; label: (iri: string) => string; canAct: boolean; resolve: Resolve; busy: boolean }) {
  const t = useT();
  const f = p.fact as ProposedFact;
  const confidence = typeof p.confidence === "number" ? p.confidence : null;
  return (
    <div className="text-sm" data-testid="review-fact">
      <div className="flex flex-wrap items-center gap-2">
        <BlockLink iri={f.block} workspaceId={workspaceId}>{label(f.block)}</BlockLink>
        <span className="font-medium" data-testid="review-fact-value">
          {kindLabel(f.kind)} {f.value} {unitSymbol(f.unit || null)}
        </span>
        {f.about && <span className="text-xs text-neutral-500" title={f.about}>{t("review.fact.about", { term: label(f.about) })}</span>}
        {confidence !== null && <span className="text-xs text-neutral-500">{t("review.edge.confidence", { value: confidence.toFixed(2) })}</span>}
        <span className="rounded border border-dashed border-neutral-400 px-1 text-[10px] uppercase text-neutral-500">{t("review.edge.derived")}</span>
      </div>
      {p.evidence ? <blockquote className="mt-1 border-l-2 border-neutral-300 pl-2 text-neutral-600 dark:text-neutral-400">{String(p.evidence)}</blockquote> : null}
      {canAct && (
        <div className="mt-2 flex gap-2">
          <Button onClick={() => resolve(item, "promote")} disabled={busy} data-testid="review-promote">{t("review.fact.promote")}</Button>
          <Button variant="danger" onClick={() => resolve(item, "reject")} disabled={busy} data-testid="review-reject">{t("review.edge.reject")}</Button>
        </div>
      )}
    </div>
  );
}

/** [M31] A quantity whose unit, as written, is not exactly one unit KSS knows for its kind. */
function FactUnitItem({ item, p, workspaceId, label, canAct, resolve, busy }: { item: ReviewItem; p: Record<string, unknown>; workspaceId: string; label: (iri: string) => string; canAct: boolean; resolve: Resolve; busy: boolean }) {
  const t = useT();
  const candidates = (p.candidates as string[] | undefined) ?? [];
  return (
    <div className="text-sm" data-testid="review-fact-unit">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">
          {kindLabel(String(p.kind))} {String(p.value)} <span className="font-normal">“{String(p.written)}”</span>
        </span>
        {p.context ? <span className="text-xs text-neutral-500">{t("review.fact.about", { term: String(p.context) })}</span> : null}
        <span className="text-xs text-neutral-500">{t("review.grounding.in")}</span>
        <BlockLink iri={String(p.block)} workspaceId={workspaceId}>{label(String(p.block))}</BlockLink>
      </div>
      <div className="mt-1 text-xs text-neutral-500">{t(p.status === "ambiguous" ? "review.factUnit.ambiguous" : "review.factUnit.unknown")}</div>
      {p.evidence ? <blockquote className="mt-1 border-l-2 border-neutral-300 pl-2 text-neutral-600 dark:text-neutral-400">{String(p.evidence)}</blockquote> : null}
      {canAct && candidates.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {candidates.map((u) => (
            <Button key={u} variant="secondary" size="sm" title={u} onClick={() => resolve(item, "choose_candidate", { unit: u })} disabled={busy} data-testid="review-choose-unit">
              {unitSymbol(u)}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

interface VersionCandidate {
  opId: string;
  peer: string;
  userId: string;
  userName: string;
  lamport: number;
  value: unknown;
}

function shortValue(value: unknown, empty: string): string {
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return text && text.length > 140 ? `${text.slice(0, 140)}…` : (text || empty);
}

/** Concurrent writes to one LWW key (spec §17.3): pick the version that wins for everyone. */
function ConflictItem({ item, p, workspaceId, canAct, resolve, busy }: { item: ReviewItem; p: Record<string, unknown>; workspaceId: string; canAct: boolean; resolve: Resolve; busy: boolean }) {
  const t = useT();
  const candidates = (p.candidates as VersionCandidate[] | undefined) ?? [];
  return (
    <div className="text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">
          <Rich text={t("review.conflict.title", { key: String(p.key) })} strong="font-mono text-xs font-normal" />
        </span>
        <span className="text-xs text-neutral-500">{String(p.blockType ?? "")} · {String(p.path ?? "")}</span>
        {item.crdtDocId && item.treeId && (
          <BlockLink iri={`urn:km:blk:${item.crdtDocId}:${item.treeId}`} workspaceId={workspaceId}>{t("review.openBlock")}</BlockLink>
        )}
      </div>
      <ul className="mt-2 space-y-1">
        {candidates.map((c) => (
          <li key={c.opId} className="flex items-start gap-2" data-testid="conflict-candidate">
            <span className="w-32 shrink-0 truncate text-xs text-neutral-500" title={t("review.conflict.peer", { peer: c.peer })}>{c.userName || c.userId || t("review.conflict.peer", { peer: c.peer })}</span>
            <code className="min-w-0 grow break-all text-xs">{shortValue(c.value, t("review.conflict.empty"))}</code>
            {canAct && (
              <Button variant="ghost" onClick={() => resolve(item, "pick_version", { opId: c.opId })} disabled={busy} data-testid="review-pick-version">
                {t("review.conflict.use")}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A deprecated ontology term still used by links (spec §14.8). */
function DeprecatedTerm({ item, p, label, canAct, canWrite, resolve, busy }: { item: ReviewItem; p: Record<string, unknown>; label: (iri: string) => string; canAct: boolean; canWrite: boolean; resolve: Resolve; busy: boolean }) {
  const t = useT();
  const replacement = typeof p.replacement === "string" ? p.replacement : null;
  const res = (p.resolution ?? {}) as { action?: string; reverted?: boolean; changes?: unknown[] };
  if (res.action === "auto_migration") {
    // [M33] a migration KSS applied by itself: what it changed, and the way back (spec §14.8, Q94)
    return (
      <div className="text-sm" data-testid="review-auto-migration">
        <div className="font-medium">
          <span title={String(p.term)}>{label(String(p.term))}</span> → <span title={String(replacement)}>{label(String(replacement))}</span>
        </div>
        <div className="mt-1 text-neutral-600 dark:text-neutral-400">
          {res.reverted ? t("review.deprecated.reverted") : t("review.deprecated.auto", { count: (res.changes ?? []).length })}
        </div>
        {canWrite && !res.reverted && (
          <div className="mt-2">
            <Button variant="secondary" onClick={() => resolve(item, "revert")} disabled={busy} data-testid="review-revert-migration">
              {t("review.deprecated.revert")}
            </Button>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium" title={String(p.term)}>{label(String(p.term))}</span>
        <span className="text-xs text-neutral-500">{t("review.deprecated.usedBy", { count: Number(p.count ?? 0) })}</span>
      </div>
      <div className="mt-1 text-neutral-600 dark:text-neutral-400">
        {replacement ? <>{t("review.deprecated.replacement")} <span title={replacement}>{label(replacement)}</span></> : t("review.deprecated.noReplacement")}
      </div>
      {typeof p.reason === "string" && p.reason && replacement && (
        <div className="mt-1 text-xs text-neutral-500" data-testid="review-migration-reason">{t("review.deprecated.notAuto", { reason: t(MIGRATION_REASON[p.reason] ?? "review.deprecated.reason.no_replacement") })}</div>
      )}
      {canAct && replacement && (
        <div className="mt-2">
          <Button onClick={() => resolve(item, "apply_migration")} disabled={busy} data-testid="review-apply-migration">
            {t("review.deprecated.apply")}
          </Button>
        </div>
      )}
    </div>
  );
}

/** [M33] why the server did not replace a term by itself (codes of `km/ontology/migration.py`) */
const MIGRATION_REASON: Record<string, string> = {
  no_replacement: "review.deprecated.reason.no_replacement",
  several_replacements: "review.deprecated.reason.several_replacements",
  cycle: "review.deprecated.reason.cycle",
  chain_too_long: "review.deprecated.reason.chain_too_long",
  not_imported: "review.deprecated.reason.not_imported",
  kind_mismatch: "review.deprecated.reason.kind_mismatch",
  breaks_shape: "review.deprecated.reason.breaks_shape",
  auto_off: "review.deprecated.reason.auto_off",
};

interface Candidate {
  iri: string;
  label: string;
  score: number;
  source: "ontology" | "concept";
}

function GroundingItem({ item, p, workspaceId, label, canAct, resolve, busy }: { item: ReviewItem; p: Record<string, unknown>; workspaceId: string; label: (iri: string) => string; canAct: boolean; resolve: Resolve; busy: boolean }) {
  const t = useT();
  const candidates = (p.candidates as Candidate[] | undefined) ?? [];
  const [conceptLabel, setConceptLabel] = useState(String(p.label ?? ""));
  return (
    <div className="text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium" data-testid="review-grounding-label">“{String(p.label)}”</span>
        <span className="text-xs text-neutral-500" title={t("review.grounding.typeTitle")} data-testid="review-grounding-type">
          {t("review.grounding.suggested", { type: String(p.expectedType) })}
        </span>
        <span className="text-xs text-neutral-500">{t("review.grounding.in")}</span>
        <BlockLink iri={String(p.block)} workspaceId={workspaceId}>{label(String(p.block))}</BlockLink>
        <span className="text-xs text-neutral-500">{p.decision === "new" ? t("review.grounding.noMatch") : t("review.grounding.best", { score: Number(p.score).toFixed(2) })}</span>
      </div>
      {candidates.length > 0 && (
        <ul className="mt-2 space-y-1">
          {candidates.map((c) => (
            <li key={c.iri} className="flex items-center gap-2">
              <span className="w-10 text-right text-xs tabular-nums text-neutral-500">{c.score.toFixed(2)}</span>
              <span title={c.iri}>{c.label}</span>
              <span className="text-xs text-neutral-500">{c.source === "concept" ? t("linkDialog.workspaceConcept") : c.iri.replace(/^.*[#/]/, "")}</span>
              {canAct && (
                <Button variant="ghost" onClick={() => resolve(item, "choose_candidate", { iri: c.iri })} disabled={busy} data-testid="review-choose">
                  {t("review.grounding.choose")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canAct && (
        <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); resolve(item, "create_concept", { prefLabel: conceptLabel }); }}>
          <Input value={conceptLabel} onChange={(e) => setConceptLabel(e.target.value)} className="max-w-xs" aria-label={t("review.grounding.conceptLabel")} />
          <Button type="submit" variant="ghost" disabled={busy || !conceptLabel.trim()} data-testid="review-create-concept">{t("review.grounding.createConcept")}</Button>
        </form>
      )}
    </div>
  );
}
